<template>
  <div class="ofs-transactions-new-item">
    <div class="ofs-transactions-new-item__date">
      <DSDateField
        v-model="date"
        size="sm"
        :readonly
        aria-label="Transaction date"
        class="flex-auto"
      />
      <UIButton
        variant="danger"
        fill="light"
        size="sm"
        :leading="TrashIcon"
        @click="handleClickDelete"
      />
    </div>
    <div class="ofs-transactions-new-item__type">
      {{ type }}
    </div>
    <div class="ofs-transactions-new-item__entries">
      <Entries
        :transaction-settings
        :transaction
      />
    </div>
    <UIButton
      v-tooltip.top="'Delete transaction'"
      variant="danger"
      fill="light"
      size="sm"
      :leading="TrashIcon"
      class="ofs-transactions-new-item__delete"
      @click="handleClickDelete"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue'

import { TransactionSettings } from '@types'
import { OrganizeFamilyStructureTransaction } from './utils/types'

import { READONLY_MODE } from '@/const'

import { getCurrentDate } from '@/helpers/dates'
import useAnalyticsStore from '@/store/analytics'

import Entries from './components/Entries.vue'

import { TrashIcon } from '@heroicons/vue/24/outline'
import { UIButton } from '@ui/buttons'
import { DSDateField } from '@ds'

type Props = {
  dates: Map<string, string>
  transaction: OrganizeFamilyStructureTransaction
  transactionSettings?: TransactionSettings
}

type Emits = {
  update: [date: string, accountId: string, assetId: string]
  delete: []
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const analyticsStore = useAnalyticsStore()

const readonly = inject<boolean>(READONLY_MODE)  

const analyticsDate = computed(() => analyticsStore.getDate)

const date = computed({
  get() {
    return props.dates.get(`${props.transaction.accountId}-${props.transaction.assetId}`) || analyticsDate.value || getCurrentDate({})
  },
  set(date) {
    if (!date) return
    props.dates.set(`${props.transaction.accountId}-${props.transaction.assetId}`, date)
    emit('update', date, props.transaction.accountId, props.transaction.assetId)
  },
})
const type = computed(() => props.transactionSettings?.name || '')

const handleClickDelete = () => emit('delete')
</script>

<style>
.ofs-transactions-new-item {
  @apply flex flex-col sm:flex-row sm:items-center gap-2;
  @apply border border-common-border hover:border-primary-selected dark:hover:border-gray-500;
  @apply bg-body rounded-md;
  @apply py-2.5 px-2;
  @apply text-sm;
  @apply text-gray-400;

  &__date {
    @apply w-full;
    @apply flex items-center justify-between gap-2;
    @apply sm:w-34;

    .ui-button {
      @apply sm:hidden;
    }
  }

  &__type {
    @apply font-medium;
    @apply text-primary-active dark:text-indigo-300;
    @apply whitespace-nowrap;
  }

  &__entries {
    @apply flex-auto;
    @apply overflow-hidden;
  }

  &__delete {
    @apply hidden sm:block;
  }
}
</style>
